/** De Live Feed als groeiende lijst: kop verversen, onderaan ouder laden. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'
import { FEED_DAGEN, FEED_KNOP_MAX_BLADZIJDEN, feedUrl, mergeFeed, nieuweRegels, reiktTot, vervangKop } from './liveFeed'
import type { FeedRow } from './liveFeed'

const lijst = <T,>(d: T[] | { data: T[] } | null | undefined): T[] =>
  Array.isArray(d) ? d : (d?.data ?? [])

export interface LiveFeed<T> {
  rows: T[]
  loading: boolean
  hasMore: boolean
  loadingOlder: boolean
  /** Bezig met in één keer doorladen ("Show 7 days"). */
  loadingDays: boolean
  refetch: () => void
  loadOlder: () => void
  /** Laadt oudere bladzijden achter elkaar tot `dagen` terug (of het einde). */
  loadDays: (dagen: number) => void
}

export function useLiveFeed<T extends FeedRow>(
  feed: 'dashboard' | 'chatter',
  platform: string,
  /** 06-10-2026: gekozen model (slug) of ''; een ander model is een nieuwe lijst. */
  model = '',
): LiveFeed<T> {
  const [rows, setRows] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [loadingDays, setLoadingDays] = useState(false)
  // Een andere feed, een ander platform of model is een nieuwe lijst; antwoorden
  // van vóór de wissel tellen niet meer.
  const generatie = useRef(0)
  const sinds = useRef(new Date(Date.now() - FEED_DAGEN * 86_400_000).toISOString())
  const rowsRef = useRef<T[]>([])
  const hasMoreRef = useRef(true)
  const ouderBezig = useRef(false)
  /** 19-10-2026 (Jordy): de generatie waarvoor de kop nu wordt opgehaald (null =
   *  niets). De 2,5 s-poll start geen tweede verzoek zolang dit loopt. */
  const kopBezig = useRef<number | null>(null)

  useEffect(() => {
    rowsRef.current = rows
  }, [rows])
  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  const refetch = useCallback(() => {
    const mijn = generatie.current
    if (kopBezig.current === mijn) return
    kopBezig.current = mijn
    api
      .get<T[] | { data: T[] }>(feedUrl(feed, platform, sinds.current, undefined, model))
      .then((d) => {
        if (mijn !== generatie.current) return
        setRows((vorige) => vervangKop(vorige, lijst(d)))
      })
      .catch(() => {})
      .finally(() => {
        if (kopBezig.current === mijn) kopBezig.current = null
        if (mijn === generatie.current) setLoading(false)
      })
  }, [feed, platform, model])

  const loadOlder = useCallback(() => {
    if (ouderBezig.current || !hasMoreRef.current) return
    const huidig = rowsRef.current
    const before = String(huidig[huidig.length - 1]?.timestamp ?? '')
    if (!before) return
    ouderBezig.current = true
    setLoadingOlder(true)
    const mijn = generatie.current
    api
      .get<T[] | { data: T[] }>(feedUrl(feed, platform, sinds.current, before, model))
      .then((d) => {
        if (mijn !== generatie.current) return
        const ouder = lijst(d)
        // Het einde: een oudere bladzijde die niets nieuws meer brengt.
        if (nieuweRegels(rowsRef.current, ouder) === 0) setHasMore(false)
        setRows((vorige) => mergeFeed(vorige, ouder))
      })
      .catch(() => {})
      .finally(() => {
        ouderBezig.current = false
        if (mijn === generatie.current) setLoadingOlder(false)
      })
  }, [feed, platform, model])

  const loadDays = useCallback(async (dagen: number) => {
    if (ouderBezig.current) return
    const grens = Date.now() - dagen * 86_400_000
    const mijn = generatie.current
    ouderBezig.current = true
    setLoadingOlder(true)
    setLoadingDays(true)
    try {
      for (let i = 0; i < FEED_KNOP_MAX_BLADZIJDEN; i++) {
        const huidig = rowsRef.current
        if (!hasMoreRef.current || reiktTot(huidig, grens)) break
        const before = String(huidig[huidig.length - 1]?.timestamp ?? '')
        if (!before) break
        const d = await api.get<T[] | { data: T[] }>(
          feedUrl(feed, platform, sinds.current, before, model))
        if (mijn !== generatie.current) return
        const ouder = lijst(d)
        const nieuw = nieuweRegels(rowsRef.current, ouder)
        // De ref meteen bijwerken: de volgende ronde van deze lus moet de
        // nieuwe oudste regel zien, niet wachten tot React opnieuw rendert.
        rowsRef.current = mergeFeed(rowsRef.current, ouder)
        setRows((vorige) => mergeFeed(vorige, ouder))
        if (nieuw === 0) {
          hasMoreRef.current = false
          setHasMore(false)
          break
        }
      }
    } catch {
      // Een mislukte bladzijde laat staan wat er al is; scrollen kan nog.
    } finally {
      ouderBezig.current = false
      if (mijn === generatie.current) {
        setLoadingOlder(false)
        setLoadingDays(false)
      }
    }
  }, [feed, platform, model])

  useEffect(() => {
    generatie.current += 1
    rowsRef.current = []
    hasMoreRef.current = true
    ouderBezig.current = false
    setRows([])
    setHasMore(true)
    setLoadingOlder(false)
    setLoadingDays(false)
    setLoading(true)
    refetch()
  }, [refetch])

  return { rows, loading, hasMore, loadingOlder, loadingDays, refetch, loadOlder, loadDays }
}
